import { MobileSearchWidget } from '@/components/search-widget/MobileSearchWidget';
import { RegularSearchWidget } from '@/components/search-widget/RegularSearchWidget';
import {
  salesPointConfigurationSelector,
  tenantSelector,
} from '@/features/configuration/configurationSelector';
import {
  outboundOrInboundJourneysLoadingSelector,
  purchaseFlowBookingCreationLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { purchaseFlowBookingIdSelector } from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useValidatedChangeBookingPromoCodes } from '@/hooks/useValidatedChangeBookingPromoCodes';
import { TransAlert } from '@/i18n/trans/alert';
import { useSelector } from '@/store/utils';
import type { BookedTripDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { buildChangeBookingSearchFormValues } from '@/utils/changeTrips';
import { getTenantBrand } from '@/utils/color';
import { getTenantConfiguration } from '@/utils/tenant';
import { parseUrlSearchParams } from '@/utils/urlSearchParams';
import { Divider } from '@fleet/webshop-ui';
import {
  SearchWidget,
  type SearchFormValues,
} from '@turnit-ride-ui/webshop-search-widget';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function flattenFieldErrors(
  node: FieldErrors<SearchFormValues> | unknown,
  prefix = ''
): string[] {
  if (!node || typeof node !== 'object') return [];

  if (typeof (node as { message?: unknown }).message === 'string') {
    const { message } = node as { message: string };
    return [`${prefix || '(root)'}: ${message}`];
  }

  return Object.entries(node as Record<string, unknown>).flatMap(
    ([key, value]) =>
      flattenFieldErrors(value, prefix ? `${prefix}.${key}` : key)
  );
}

export const SearchWidgetWrapper = () => {
  const { pathname, search } = useLocation();
  const tenant = useSelector(tenantSelector);
  const configuration = getTenantConfiguration(tenant.value);
  const { brandColors } = getTenantBrand(tenant);
  const { handleSubmit, currentSearchFormValues } = useSearchWidget();
  const salesPointConfiguration = useSelector(salesPointConfigurationSelector);
  const purchaseBookingId = useSelector(purchaseFlowBookingIdSelector);
  const outboundOrInboundJourneysLoading = useSelector(
    outboundOrInboundJourneysLoadingSelector
  );
  const bookingLoading = useSelector(
    purchaseFlowBookingCreationLoadingSelector
  );
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const navigate = useNavigate();
  const [isAutoSubmitted, setIsAutoSubmitted] = useState(false);
  const searchParams = new URLSearchParams(search);
  const defaultProductType = searchParams.get('productType') as
    | 'ticket'
    | 'travelPass'
    | null;

  // When the user arrives at /purchase via the Change-Trips flow, prefill
  // the search form with the original trip's parameters: origin,
  // destination, departure date, the booking's passenger roster (locked),
  // and re-validated promo codes. Only the passenger picker is locked —
  // everything else stays editable so the customer can adjust the search.
  const changeBookingSelection = useChangeBookingSelection();
  const validatedPromoCodes = useValidatedChangeBookingPromoCodes();
  const changeBookingFormValues: SearchFormValues | undefined = useMemo(() => {
    if (!changeBookingSelection.hasSelection) return undefined;
    const selectedTrips = [
      changeBookingSelection.selectedOutboundTrip,
      changeBookingSelection.selectedInboundTrip,
    ].filter((trip): trip is BookedTripDTO => trip !== null);
    const prefill = buildChangeBookingSearchFormValues(
      changeBookingSelection.selectedPassengers,
      selectedTrips,
      salesPointConfiguration?.passengerTypes,
      validatedPromoCodes
    );
    if (!prefill.passengers || prefill.passengers.length === 0)
      return undefined;
    return { ...currentSearchFormValues, ...prefill } as SearchFormValues;
  }, [
    changeBookingSelection.hasSelection,
    changeBookingSelection.selectedPassengers,
    changeBookingSelection.selectedOutboundTrip,
    changeBookingSelection.selectedInboundTrip,
    salesPointConfiguration?.passengerTypes,
    validatedPromoCodes,
    currentSearchFormValues,
  ]);

  // Auto-fire the initial exchange search when entering /purchase in
  // change-booking mode. Mirrors the URL-param auto-submit branch below;
  // both share the `isAutoSubmitted` flag so they can't both fire. The
  // promo-code validation runs in parallel and lands in formValues later;
  // we don't gate the auto-submit on it (rare for the first search to
  // need them, and we'd rather show results quickly).
  useEffect(() => {
    if (isAutoSubmitted) return;
    if (!changeBookingFormValues) return;
    if (
      !changeBookingFormValues.origin?.value ||
      !changeBookingFormValues.destination?.value ||
      !changeBookingFormValues.departureDate ||
      !changeBookingFormValues.passengers?.length
    ) {
      return;
    }
    handleSubmit(changeBookingFormValues);
    setIsAutoSubmitted(true);
  }, [changeBookingFormValues, handleSubmit, isAutoSubmitted]);

  useEffect(() => {
    if (salesPointConfiguration?.currencies?.length && !purchaseBookingId) {
      const {
        productType,
        origin,
        destination,
        departureDate,
        returnDate,
        passengers,
        promoCodes,
      } = parseUrlSearchParams(salesPointConfiguration.passengerTypes);

      if (
        origin?.value &&
        destination?.value &&
        departureDate &&
        passengers &&
        productType &&
        !isAutoSubmitted
      ) {
        handleSubmit({
          productType,
          origin,
          destination,
          departureDate,
          returnDate,
          passengers,
          promoCodes,
        });
      }

      setIsAutoSubmitted(true);
    }
  }, [
    handleSubmit,
    isAutoSubmitted,
    purchaseBookingId,
    salesPointConfiguration?.currencies?.length,
    salesPointConfiguration?.passengerTypes,
  ]);

  const handleProductTypeChange = useCallback(() => {
    if (pathname !== '/purchase') {
      navigate('/purchase');
    }
  }, [pathname, navigate]);

  const handleValidationError = useCallback(
    (errors: FieldErrors<SearchFormValues>) => {
      const details = flattenFieldErrors(errors);
      toast.error(
        <div className="flex flex-col gap-1">
          <TransAlert i18nKey="searchFormValidationFailed" />
          {details.length > 0 && (
            <ul className="font-mono list-disc pl-4 text-xs">
              {details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );
    },
    []
  );

  const Component = isLaptopOrBigger ? RegularSearchWidget : MobileSearchWidget;

  return (
    <div className="sticky top-0 z-50 bg-background">
      <Component>
        <SearchWidget
          baseUrl={import.meta.env.VITE_APP_PROXY_TARGET}
          onSubmit={handleSubmit}
          onProductTypeChange={handleProductTypeChange}
          onValidationError={handleValidationError}
          height="small"
          lockedFields={{
            submitButton: outboundOrInboundJourneysLoading || bookingLoading,
            passengers: changeBookingSelection.hasSelection,
            productType: changeBookingSelection.hasSelection,
          }}
          brandColors={brandColors}
          backgroundTheme="light"
          stopsSearchingStrategy={configuration?.stopsSearchingStrategy}
          formValues={changeBookingFormValues ?? currentSearchFormValues}
          submitOnPromoCodeChange={pathname !== '/purchase'}
          defaultProductType={defaultProductType ?? undefined}
        />
      </Component>
      <Divider variant="medium" />
    </div>
  );
};
